import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import CryptoJS from 'crypto-js';

const AppContext = createContext();
const SECRET_KEY = 'dpod-ameya-web-app-v.1.0';

// Shared encrypt/decrypt helpers to eliminate duplication
function decryptFromSession(key, fallback = null) {
    const stored = sessionStorage.getItem(key);
    if (!stored) return fallback;
    try {
        const { encryptedData, hash } = JSON.parse(stored);
        const computedHash = CryptoJS.SHA256(encryptedData + SECRET_KEY).toString();
        if (computedHash !== hash) {
            console.warn(`Hash mismatch for ${key}: possible tampering`);
            return fallback;
        }
        const bytes = CryptoJS.AES.decrypt(encryptedData, SECRET_KEY);
        return JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
    } catch (err) {
        console.error(`Failed to decrypt ${key}:`, err);
        return fallback;
    }
}

function encryptToSession(key, value) {
    if (value != null) {
        const encryptedData = CryptoJS.AES.encrypt(JSON.stringify(value), SECRET_KEY).toString();
        const hash = CryptoJS.SHA256(encryptedData + SECRET_KEY).toString();
        sessionStorage.setItem(key, JSON.stringify({ encryptedData, hash }));
    } else {
        sessionStorage.removeItem(key);
    }
}

export function AppProvider({ children }) {

    const [dataLoading, setDataLoading] = useState(false)
    const [permissionStatus, setPermissionStatus] = useState(false)
    const [billingStatus, setBillingStatus] = useState(false)
    const [apiKeyStatus, setApiKeyStatus] = useState(false)
    const [isOrganizationOwner, setIsOrganizationOwner] = useState(false);

    const new_prject_initial_data = { status: false, project_id: null }
    const [newProjectData, setNewProjectData] = useState(new_prject_initial_data);

    const [selectedOrganization, setSelectedOrganization] = useState(() => decryptFromSession('selected_organization'));
    const [selectedService, setSelectedService] = useState(() => decryptFromSession('selected_service'));
    const [selectedWorkspace, setSelectedWorkspace] = useState(() => decryptFromSession('selected_space'));
    const [selectedProject, setSelectedProject] = useState(() => decryptFromSession('selected_project'));

    const initialAuthData = {
        user_type: null,
        request_type: null,
        organization_id: null,
        service_id: null,
        workspace_id: null,
        project_id: null,
        billing_period_type: null,
        collection_service_type: null,
        service_message: null
    };

    const [authData, setAuthData] = useState(() => {
        const stored = decryptFromSession('authData', initialAuthData);
        return { ...initialAuthData, ...stored };
    });

    const updateAuthData = useCallback(
        (updates) => setAuthData(prev => ({ ...prev, ...updates })),
        []
    );

    useEffect(() => { encryptToSession('selected_organization', selectedOrganization); }, [selectedOrganization]);
    useEffect(() => { encryptToSession('selected_service', selectedService); }, [selectedService]);
    useEffect(() => { encryptToSession('selected_space', selectedWorkspace); }, [selectedWorkspace]);
    useEffect(() => { encryptToSession('selected_project', selectedProject); }, [selectedProject]);
    useEffect(() => { encryptToSession('authData', authData); }, [authData]);

    return (
        <AppContext.Provider
            value={{
                selectedOrganization, setSelectedOrganization,
                selectedService, setSelectedService,
                selectedWorkspace, setSelectedWorkspace,
                selectedProject, setSelectedProject,
                newProjectData, setNewProjectData,
                permissionStatus, setPermissionStatus,
                dataLoading, setDataLoading,
                billingStatus, setBillingStatus,
                isOrganizationOwner, setIsOrganizationOwner,
                apiKeyStatus, setApiKeyStatus,
                authData, setAuthData,
                initialAuthData, updateAuthData
            }}
        >
            {children}
        </AppContext.Provider>
    );
}

export function useAppContext() {
    return useContext(AppContext);
}
